/**
 * Smart human-readable label for an element — the agentation-style
 * "button.submit-btn" header shown in the annotation card.
 *
 * Rules:
 *   - For `<button>`, `<a>`, `<summary>`, `<label>`: prefer the visible
 *     text content (trimmed, truncated). The format is
 *     `tag.class-list` when a stable class is present, otherwise the
 *     trimmed text content alone.
 *   - For `<input>`, `<textarea>`, `<select>`: include the `name` or
 *     `placeholder` when present.
 *   - For everything else: tag plus first stable class.
 *
 * The label is intentionally lossy — it's a UI affordance, not a
 * selector. The strict selector lives in `getCssSelector`.
 */
import { stableClasses } from "./elementPath.js";
const truncate = (s, n = 40) => s.length > n ? `${s.slice(0, n - 1)}…` : s;
const visibleText = (el) => {
    const t = (el.textContent ?? "").trim().replace(/\s+/g, " ");
    return truncate(t);
};
export const smartLabel = (el) => {
    const tag = el.tagName.toLowerCase();
    const firstClass = stableClasses(el)[0];
    const base = firstClass ? `${tag}.${firstClass}` : tag;
    switch (tag) {
        case "button":
        case "a":
        case "summary":
        case "label": {
            const text = visibleText(el);
            if (text)
                return `${base} — "${text}"`;
            return base;
        }
        case "input":
        case "textarea":
        case "select": {
            const input = el;
            const name = input.getAttribute("name");
            const placeholder = input.placeholder;
            const aria = input.getAttribute("aria-label");
            const hint = name ?? placeholder ?? aria;
            return hint ? `${base} — ${hint}` : base;
        }
        case "img": {
            const alt = el.getAttribute("alt");
            return alt ? `${base} — "${alt}"` : base;
        }
        default:
            return base;
    }
};
//# sourceMappingURL=smartLabel.js.map